import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { NeoButton } from "../../shared/components";
import ScheduleDelaySection, { ScheduleDelayOptions } from "./ScheduleDelaySection";

export type DefaultGroup = { name: string; checked: boolean };

export interface AddToGroupsConfig extends ScheduleDelayOptions {
  defaultGroups: DefaultGroup[];
  technician_instruction: string;
}

const DEFAULT_CONFIG: AddToGroupsConfig = {
  defaultGroups: [
    { name: "All Employees", checked: false },
    { name: "Company Announcements", checked: false },
    { name: "VPN Users", checked: false },
  ],
  technician_instruction: "",
};

interface AddToGroupsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: AddToGroupsConfig) => void;
  initialConfig?: AddToGroupsConfig | null;
  schedulerEnabled?: boolean;
}

const AddToGroupsModal: React.FC<AddToGroupsModalProps> = ({
  open,
  onClose,
  onSave,
  initialConfig,
  schedulerEnabled = false,
}) => {
  const [config, setConfig] = useState<AddToGroupsConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    if (open) {
      setConfig(initialConfig || DEFAULT_CONFIG);
    }
  }, [open, initialConfig]);

  const handleDefaultGroupToggle = (index: number, checked: boolean) => {
    setConfig((prev) => ({
      ...prev,
      defaultGroups: prev.defaultGroups.map((g, i) =>
        i === index ? { ...g, checked } : g
      ),
    }));
  };

  const handleAddDefaultGroup = () => {
    setConfig((prev) => ({
      ...prev,
      defaultGroups: [...prev.defaultGroups, { name: "", checked: true }],
    }));
  };

  const handleDefaultGroupNameChange = (index: number, name: string) => {
    setConfig((prev) => ({
      ...prev,
      defaultGroups: prev.defaultGroups.map((g, i) =>
        i === index ? { ...g, name } : g
      ),
    }));
  };

  const handleScheduleDelayChange = (key: keyof ScheduleDelayOptions, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#705D4A" } },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEnforceFocus disableScrollLock>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 600,
        }}
      >
        <GroupIcon sx={{ color: "#705D4A" }} />
        Add User to Groups
      </DialogTitle>
      <DialogContent
        sx={{
          fontFamily: "var(--theo-font-sans)",
          "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <ScheduleDelaySection
            enabled={schedulerEnabled}
            options={config}
            onChange={handleScheduleDelayChange}
          />

          {/* Default Groups */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Default Groups
            </Typography>
            {config.defaultGroups.map((g, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={g.checked}
                      onChange={(e) => handleDefaultGroupToggle(i, e.target.checked)}
                      sx={{ "&.Mui-checked": { color: "#705D4A" } }}
                    />
                  }
                  label=""
                />
                <TextField
                  size="small"
                  placeholder="Group name"
                  value={g.name}
                  onChange={(e) => handleDefaultGroupNameChange(i, e.target.value)}
                  sx={{ flex: 1, ...textFieldSx }}
                  inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                />
                {config.defaultGroups.length > 1 && (
                  <IconButton size="small" onClick={() => {
                    setConfig((prev) => ({
                      ...prev,
                      defaultGroups: prev.defaultGroups.filter((_, idx) => idx !== i),
                    }));
                  }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddDefaultGroup}
              sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none", mt: 0.5 }}
            >
              Add group
            </Button>
          </Box>

          {/* Technician instruction (for LLM) */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Instruction for Theo AI
            </Typography>
            <TextField
              multiline
              minRows={3}
              size="small"
              placeholder="e.g. If the ticket company differs from the default groups, confirm which groups to use before running Entra group membership."
              value={config.technician_instruction}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  technician_instruction: e.target.value,
                }))
              }
              sx={{ width: "100%", ...textFieldSx }}
              inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
              InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
            />
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: "var(--theo-font-sans)", color: "#666" }}>
          Cancel
        </Button>
        <NeoButton variant="neo" onClick={handleSave}>
          Save
        </NeoButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddToGroupsModal;
